/**
 * FlowTemplateGallery – Modal de templates de fluxo.
 *
 * Duas abas:
 *  - Curados: templates estáticos (Lançamento, Boas-vindas, Carrinho, etc).
 *    Aplicam direto no canvas; o usuário aciona "Materializar drafts" pra
 *    criar os e-mails reais.
 *  - IA: gerados sob demanda pelo LLM com briefing.
 */

import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sparkles,
  Loader2,
  Mail,
  Heart,
  RefreshCw,
  Rocket,
  ShoppingCart,
  Cake,
  ShieldCheck,
  ChevronLeft,
  ChevronRight,
  Wand2,
  Layers,
  AlertCircle,
} from "lucide-react";
import CampaignBriefing, { type BriefingData } from "./CampaignBriefing";
import { FLOW_TEMPLATES, type FlowTemplate } from "@/data/flowTemplates";

const AI_TEMPLATES = [
  { type: "boas_vindas" as const, label: "Boas-vindas", icon: Mail, description: "Série de emails de boas-vindas para novos contatos", defaultEmails: 5, defaultDays: 14 },
  { type: "nutricao" as const, label: "Nutrição", icon: Heart, description: "Sequência de nutrição de leads com conteúdo educativo", defaultEmails: 7, defaultDays: 30 },
  { type: "reengajamento" as const, label: "Reengajamento", icon: RefreshCw, description: "Recuperar contatos inativos com ofertas e incentivos", defaultEmails: 4, defaultDays: 10 },
  { type: "lancamento" as const, label: "Lançamento", icon: Rocket, description: "Campanha completa de lançamento de produto (pré, durante, pós)", defaultEmails: 8, defaultDays: 21 },
  { type: "recuperacao" as const, label: "Recuperação", icon: ShoppingCart, description: "Recuperar leads que não converteram", defaultEmails: 3, defaultDays: 7 },
] as const;

type AITemplateType = typeof AI_TEMPLATES[number]["type"];

const CATEGORY_ICONS: Record<FlowTemplate["category"], React.ElementType> = {
  "Boas-vindas": Mail,
  "Lançamento": Rocket,
  "Recuperação": ShoppingCart,
  "Retenção": ShieldCheck,
  "Pós-venda": Heart,
  "Datas": Cake,
};

interface FlowTemplateGalleryProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApplyTemplate: (data: { name: string; nodes: unknown[]; edges: unknown[] }) => void;
}

export default function FlowTemplateGallery({ open, onOpenChange, onApplyTemplate }: FlowTemplateGalleryProps) {
  const [tab, setTab] = useState<"curated" | "ai">("curated");
  const [selected, setSelected] = useState<AITemplateType | null>(null);
  const [briefing, setBriefing] = useState<BriefingData>({});
  const [emailCount, setEmailCount] = useState(5);
  const [durationDays, setDurationDays] = useState(14);

  const generateMutation = trpc.aiEmail.generateFlowTemplate.useMutation({
    onSuccess: (data) => {
      onApplyTemplate({ name: data.name, nodes: data.nodes, edges: data.edges });
      onOpenChange(false);
    },
  });

  function handleSelectAI(t: typeof AI_TEMPLATES[number]) {
    setSelected(t.type);
    setEmailCount(t.defaultEmails);
    setDurationDays(t.defaultDays);
  }

  function handleGenerate() {
    if (!selected) return;
    generateMutation.mutate({
      templateType: selected,
      emailCount,
      totalDurationDays: durationDays,
      productId: briefing.productId,
      audienceId: briefing.audienceId,
      briefingId: undefined,
      copyStyle: briefing.copyStyle,
    });
  }

  function handleApplyCurated(tpl: FlowTemplate) {
    // Cópia profunda para o ReactFlow não mexer no template original
    onApplyTemplate({
      name: tpl.name,
      nodes: JSON.parse(JSON.stringify(tpl.nodes)),
      edges: JSON.parse(JSON.stringify(tpl.edges)),
    });
    onOpenChange(false);
  }

  const selectedTemplate = AI_TEMPLATES.find((t) => t.type === selected);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl w-[96vw] max-h-[90vh] overflow-hidden p-0 gap-0 flex flex-col">
        {/* Header com gradient */}
        <div className="px-6 py-4 border-b bg-gradient-to-r from-indigo-50 via-violet-50 to-blue-50">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2.5">
              <div className="h-8 w-8 rounded-lg bg-indigo-600 flex items-center justify-center shadow-sm">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <div>
                <div className="text-base font-semibold">Templates de Fluxo</div>
                <p className="text-xs text-muted-foreground font-normal">
                  Comece com um template pronto ou peça à IA pra desenhar do zero, com gatilho, condições e ações.
                </p>
              </div>
            </DialogTitle>
          </DialogHeader>
        </div>

        <Tabs value={tab} onValueChange={(v) => setTab(v as "curated" | "ai")} className="flex-1 flex flex-col min-h-0">
          <div className="px-6 pt-3">
            <TabsList className="grid w-full grid-cols-2 max-w-md">
              <TabsTrigger value="curated" className="gap-2">
                <Layers className="w-3.5 h-3.5" />
                Curados
              </TabsTrigger>
              <TabsTrigger value="ai" className="gap-2">
                <Wand2 className="w-3.5 h-3.5" />
                Gerar com IA
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="curated" className="flex-1 overflow-y-auto px-6 py-4 mt-0">
            <p className="text-xs text-muted-foreground mb-3">
              Templates prontos. Após aplicar, clique em <strong>Materializar drafts</strong> para criar os e-mails reais.
            </p>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {FLOW_TEMPLATES.map((tpl) => {
                const Icon = CATEGORY_ICONS[tpl.category];
                const emailCount = tpl.nodes.filter((n) => n.type === "email_content").length;
                return (
                  <Card
                    key={tpl.key}
                    className="p-4 cursor-pointer hover:border-indigo-300 hover:bg-indigo-50/30 hover:shadow-sm transition-all"
                    onClick={() => handleApplyCurated(tpl)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="h-10 w-10 rounded-lg bg-indigo-100 flex items-center justify-center shrink-0">
                        <Icon className="w-5 h-5 text-indigo-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <h3 className="font-semibold text-sm">{tpl.name}</h3>
                          <Badge variant="outline" className="text-[10px]">
                            {tpl.kind === "campaign" ? "Campanha" : "Automação"}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{tpl.description}</p>
                        <p className="text-[10px] text-muted-foreground mt-1.5">
                          {emailCount} e-mail{emailCount === 1 ? "" : "s"} · ~{tpl.estimatedDays} dia{tpl.estimatedDays === 1 ? "" : "s"} · {tpl.suggestedCopyStyle}
                        </p>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="ai" className="flex-1 overflow-hidden px-6 py-4 mt-0 flex flex-col min-h-0">
            {!selected ? (
              <>
                <p className="text-xs text-muted-foreground mb-3">
                  Escolha o tipo de fluxo. A IA vai desenhar o grafo completo: gatilho, e-mails, esperas, condições de abertura/clique e ações de tag.
                </p>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 overflow-y-auto pr-1">
                  {AI_TEMPLATES.map((t) => {
                    const Icon = t.icon;
                    return (
                      <Card
                        key={t.type}
                        className="p-4 cursor-pointer hover:border-indigo-300 hover:bg-indigo-50/30 hover:shadow-sm transition-all group"
                        onClick={() => handleSelectAI(t)}
                      >
                        <div className="flex items-start gap-3">
                          <div className="h-10 w-10 rounded-lg bg-indigo-100 flex items-center justify-center shrink-0 group-hover:bg-indigo-200 transition-colors">
                            <Icon className="w-5 h-5 text-indigo-600" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5">
                              <h3 className="font-semibold text-sm">{t.label}</h3>
                              <ChevronRight className="w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{t.description}</p>
                            <p className="text-[10px] text-muted-foreground mt-1.5">
                              ~{t.defaultEmails} e-mails · {t.defaultDays} dias (sugestão)
                            </p>
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
                {/* Sub-header com breadcrumb */}
                <div className="flex items-center justify-between mb-4 pb-3 border-b">
                  <div className="flex items-center gap-3">
                    <Button variant="ghost" size="sm" onClick={() => setSelected(null)} className="gap-1.5 -ml-2">
                      <ChevronLeft className="w-3.5 h-3.5" />
                      Voltar
                    </Button>
                    <div className="text-xs text-muted-foreground">
                      <span className="font-medium">Gerar com IA</span>
                      <ChevronRight className="w-3 h-3 inline mx-1" />
                      <span className="text-foreground font-medium">{selectedTemplate?.label}</span>
                    </div>
                  </div>
                  <Badge variant="secondary" className="text-[10px] gap-1">
                    <Sparkles className="w-3 h-3" />
                    Powered by IA
                  </Badge>
                </div>

                {/* Layout 2 colunas: briefing à esquerda, parâmetros + ação à direita */}
                <div className="flex-1 grid lg:grid-cols-[1fr_320px] gap-5 overflow-y-auto">
                  <div className="min-h-0">
                    <CampaignBriefing value={briefing} onChange={setBriefing} />
                  </div>

                  <div className="space-y-4">
                    <Card className="p-4 bg-muted/30 space-y-3">
                      <div>
                        <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                          Parâmetros
                        </h4>
                        <div className="space-y-3">
                          <div>
                            <Label className="text-xs flex items-center justify-between mb-1">
                              Quantidade de e-mails
                              <span className="text-muted-foreground font-normal">{emailCount}</span>
                            </Label>
                            <Input
                              type="number"
                              min={2}
                              max={20}
                              value={emailCount}
                              onChange={(e) => setEmailCount(Number(e.target.value))}
                              className="h-9 text-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-xs flex items-center justify-between mb-1">
                              Duração total
                              <span className="text-muted-foreground font-normal">{durationDays} dia{durationDays === 1 ? "" : "s"}</span>
                            </Label>
                            <Input
                              type="number"
                              min={1}
                              max={365}
                              value={durationDays}
                              onChange={(e) => setDurationDays(Number(e.target.value))}
                              className="h-9 text-sm"
                            />
                          </div>
                        </div>
                      </div>
                    </Card>

                    <div className="rounded-lg border border-indigo-200/60 bg-indigo-50/40 p-3 space-y-1">
                      <p className="text-xs font-semibold text-indigo-900 flex items-center gap-1.5">
                        <Sparkles className="w-3.5 h-3.5" />
                        O que a IA vai gerar
                      </p>
                      <ul className="text-[11px] text-indigo-800/90 space-y-0.5 ml-5 list-disc">
                        <li>Gatilho compatível com o tipo</li>
                        <li>{emailCount} e-mails com assunto + preheader + preview</li>
                        <li>Esperas distribuídas em {durationDays} dias</li>
                        <li>Condições de abertura/clique com bifurcação</li>
                        <li>Ações de tag e score nos pontos-chave</li>
                        <li>Encerramentos rotulados por caminho</li>
                      </ul>
                    </div>

                    <Button
                      onClick={handleGenerate}
                      disabled={generateMutation.isPending}
                      className="w-full gap-2 h-10"
                    >
                      {generateMutation.isPending ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Gerando fluxo…
                        </>
                      ) : (
                        <>
                          <Wand2 className="w-4 h-4" />
                          Gerar fluxo completo
                        </>
                      )}
                    </Button>

                    {generateMutation.isError && (
                      <div className="rounded-md border border-destructive/30 bg-destructive/5 p-2.5 flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
                        <p className="text-[11px] text-destructive leading-snug break-words">
                          {generateMutation.error.message}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
